const wikiData = [
    {
        id: 1,
        title: "New Players Guide",
        icon: "⚔️",
        content: `
# Welcome to Critical Conquer

Your adventure begins here, warrior. Step into a classic world where character progression, equipment, hunting, and rivalries shape your journey.

## Getting Started

1. **Create Your Character** — Choose a class that matches your preferred combat style.
2. **Begin Your Journey** — Explore the starting areas, complete beginner quests, and build your first set of equipment.
3. **Meet Other Warriors** — Join a guild, make allies, and find players who can help you progress.
4. **Explore Twin City** — Visit the central hub to meet players, trade equipment, and discover important NPCs.
5. **Stay Informed** — Read the website Wiki and Discord announcements for server news, events, and changes.

## Your First Goal

Build your character step by step. Learn your skills, improve your equipment, and explore new hunting grounds as you grow stronger.

**Warrior's Tip:** Type \`/help\` in-game to check the available commands and useful shortcuts.
        `,
    },
    {
        id: 2,
        title: "Game Rules",
        icon: "📜",
        content: `
# The Code of Critical Conquer

Every warrior deserves a fair battlefield. These rules help protect our players, preserve the game environment, and keep the community enjoyable.

## 1. Cheating and Exploiting

The use of unauthorized third-party tools, hacks, or intentional abuse of game bugs is prohibited. Depending on the severity, violations may result in a permanent ban.

If you discover a bug or exploit, report it to the staff instead of taking advantage of it.

## 2. Account Security and Trading

You are responsible for protecting your account credentials and checking every trade carefully.

- Never share your password with another player.
- Verify the items and currency before confirming a trade.
- Double-check the character you are trading with.
- Be cautious when dealing with strangers.

If you voluntarily share your account or transfer items to the wrong person, recovery may not be possible. Staff assistance is not guaranteed in these cases.

## 3. Real-Money Trading (RMT)

Private real-money trading is permitted at your own risk.

However, advertising or promoting real-money trading through the following public channels is prohibited:

- World Chat
- Broadcasts
- Public in-game channels
- Public Discord channels

**First offense:** 1-week mute.

Repeated violations may result in longer punishments or additional disciplinary action.

## 4. Temporary and Permanent Bans

If you receive a temporary ban on Discord or in-game, wait until the restriction expires. Attempting to evade a temporary ban may lead to an extended punishment.

Players who believe a permanent ban was issued incorrectly may appeal through the staff team.

## 5. Staff Enforcement

Punishments depend on the nature, severity, and frequency of the violation. Actions may include warnings, mutes, temporary suspensions, or permanent account termination.

**Play fairly. Trade carefully. Respect your fellow warriors.** Help keep Critical Conquer a strong and respected community.
        `,
    },
    {
        id: 3,
        title: "Classes Guide",
        icon: "🛡️",
        content: `
# Choose Your Fighting Style

Every class brings a different approach to combat. Understanding your character's strengths will help you decide how to hunt, fight, and support your allies.

## Available Classes

### Trojan ⚔️
A close-range fighter built around aggressive combat and powerful weapon attacks. Ideal for players who enjoy direct engagements and offensive pressure.

### Warrior 🛡️
A durable combatant with defensive capabilities and shield-based equipment options. A solid choice for players who prefer a more resilient fighting style.

### Archer 🏹
A ranged specialist who attacks from a distance. Archers are useful for hunting monsters, farming resources, and maintaining space during combat.

### Water Taoist 🔮
A magic-focused class associated with healing and supporting allies. Water Taoists can be valuable companions during difficult hunts and group encounters.

### Fire Taoist 🔥
A spellcaster focused on offensive magic and ranged magical damage. Fire Taoists suit players who prefer dealing damage through powerful spells.

## Which Class Should You Choose?

- **Prefer close combat?** Try the Trojan.
- **Prefer defense?** Explore the Warrior.
- **Enjoy ranged attacks?** Consider the Archer.
- **Want to support your team?** Try the Water Taoist.
- **Prefer offensive magic?** Choose the Fire Taoist.

**Remember:** Your equipment, skills, experience, and playstyle all contribute to your character's performance.
        `,
    },
    {
        id: 4,
        title: "Boss Locations",
        icon: "🐉",
        content: `
# Boss Hunting Directory

Powerful monsters offer exciting challenges and opportunities to obtain valuable rewards. Prepare your equipment, gather your allies, and explore the known hunting grounds.

## Known Boss Areas

### Terato
**Location:** Twin City Altar  
**Coordinates:** To be confirmed.

### DBDevils
**Location:** Advance Map 3  
**Coordinates:** To be confirmed.

### MetsDove
**Location:** Advance Map 2  
**Coordinates:** To be confirmed.

### SnowBanshe
**Location:** Mystic Valley  
**Coordinates:** To be confirmed.

## Before You Hunt

- Bring enough potions and suitable equipment.
- Check the area for competing hunters.
- Coordinate with your guild or party when possible.
- Keep an eye on Discord announcements for event and spawn information.

## Spawn Information

Boss respawn times can vary depending on server configuration and scheduled events. Do not assume a fixed timer unless it has been confirmed by the server staff.

**Hunter's Tip:** Keep a record of sightings and share verified spawn information with your guild to improve your next hunt.
        `,
    },
    {
        id: 5,
        title: "Mining",
        icon: "⛏️",
        content: `
# Mining and Resource Gathering

Mining gives warriors another way to collect resources and potentially earn currency. With patience and the right tools, a quiet mining session can become a useful part of your progression.

## How to Start

1. **Find the Mining Area** — Visit the Mine Cave in Twin City.
2. **Prepare Your Pickaxe** — Make sure you have the required mining equipment.
3. **Begin Mining** — Follow the mining interaction supported by your game client.
4. **Collect Your Finds** — Check your inventory for any materials or gems you obtain.
5. **Use or Trade Resources** — Keep useful materials for upgrades or sell eligible items to other players.

## What Can You Find?

Depending on the server's item and drop configuration, mining may provide resources such as ores and gems.

- **Ores:** Materials that may be useful for progression.
- **Gems:** Potentially valuable items for equipment or trading.
- **Rare Finds:** Additional rewards, if enabled by the server.

## Stay Alert

Other players may be hunting or engaging in player-versus-player combat nearby. Protect your character and avoid carrying more valuable items than necessary.

**Miner's Tip:** Check current server information before relying on specific mining rewards or drop rates.
        `,
    },
    {
        id: 6,
        title: "Leveling Guide",
        icon: "🔥",
        content: `
# Your Road to Level 130

Every level brings you closer to stronger enemies and new challenges. Follow a progression route that matches your character's strength instead of fighting monsters that are too difficult.

## Suggested Leveling Route

### Levels 1–70
**Suggested areas:** Twin City and Phoenix Castle.

Learn your basic skills, complete available beginner quests, and gather equipment that suits your class.

### Levels 70–100
**Suggested area:** Desert City.

Look for monsters appropriate to your current level and improve your equipment as you progress.

### Levels 100–120
**Suggested areas:** Ape City and Bird Island.

Continue developing your character while collecting useful drops and improving your combat efficiency.

### Levels 120–130
**Suggested areas:** Mystic Valley and suitable boss-hunting areas.

Focus on consistent experience gains, stronger equipment, and group activities when available.

## Leveling Tips

- Use EXP bonuses and potions when they are available.
- Choose hunting areas where you can defeat monsters efficiently.
- Keep your equipment updated as enemy difficulty increases.
- Join parties or guild activities when they offer useful benefits.
- Watch for server announcements about experience events.

**Progression Tip:** The fastest route depends on your class, equipment, server rates, and available events. Adjust your hunting area when your current monsters become inefficient.
        `,
    },
    {
        id: 7,
        title: "Hunting Guide",
        icon: "🏹",
        content: `
# Master the Art of Hunting

Hunting is a core part of the Conquer experience. It helps you develop your character, gather resources, and discover items that may be valuable to other players.

## Prepare Before Leaving Town

- Carry enough HP and mana potions for your session.
- Archers should check their arrow supply when applicable.
- Repair or replace equipment when necessary.
- Choose a hunting area suited to your character's level and damage.
- Leave enough inventory space for potential drops.

## Hunt More Efficiently

### Find the Right Hunting Ground
Choose areas where monsters are plentiful and your character can defeat them consistently.

### Know Your Targets
Learn which monsters are suitable for your class and progression. Stronger enemies are not always the most efficient targets.

### Watch for Valuable Drops
Pay attention to equipment, materials, and other items that may be useful to you or other players.

### Use Available Bonuses
Blessing, Lucky Time, and other bonuses may help when supported by the current server configuration. Check their effects before planning a farming session around them.

## Turn Loot Into Progress

Keep items that improve your character and consider selling eligible extras through the Marketplace or direct player trades.

**Hunter's Tip:** Efficiency matters. A consistent hunting route with manageable enemies can be more productive than repeatedly fighting targets that take too long to defeat.
        `,
    },
    {
        id: 8,
        title: "VIP Features",
        icon: "💎",
        content: `
# VIP Membership

VIP membership provides additional convenience and progression benefits, depending on your VIP level and the features enabled on Critical Conquer.

## Available Features

### Remote Warehouse and Auto Loot
Convenience features such as Remote WH and Auto Loot may be available to eligible VIP members.

### VIP Maps
Access designated VIP hunting maps when included in your membership benefits.

### Experience Bonuses
The configured experience bonuses include:

- **VIP5:** x2 EXP
- **VIP6:** x3 EXP

### Useful Commands
Eligible players can use the \`/scroll\` command to access supported travel shortcuts.

### Additional Benefits
Higher VIP levels may provide extra convenience or drop-related benefits according to the current server configuration.

## Before Upgrading

Review the official website's VIP information to confirm the available features, requirements, and current prices.

VIP features can change as the server develops, so use the latest official announcements as your reference.

**VIP Tip:** Choose a membership level that matches the features you actually use during your adventures.
        `,
    },
    {
        id: 9,
        title: "Commands",
        icon: "⌨️",
        content: `
# In-Game Command Reference

Use supported commands to access shortcuts and useful features without navigating through every menu.

## General Commands

- **\`/help\`** — View available commands.
- **\`/stuck\`** — Use the supported recovery option if your character gets stuck or encounters a black screen.
- **\`/discord\`** — Open or display the Discord link.
- **\`/website\`** — Open or display the official website link.
- **\`/dc\`** — Disconnect from the game.

## Travel and Events

- **\`/scroll\`** — Use supported travel shortcuts for AC, DC, TC, PC, BI, TG, and HK.
- **\`/joinpvp\`** — Check or access supported PvP event features.

## Other Utilities

- **\`/rolescale\`** — Adjust the supported zoom scale from 1 to 200.
- **\`/vipmineores\`** — Use the VIP mining utility to skip ores, if available to your character.
- **\`/screenshot\`** — Take a screenshot using the server's supported feature.

## Having Trouble?

Make sure you type each command correctly and meet any VIP, location, or other requirements. Some commands may have cooldowns or may only work under specific conditions.

**Command Tip:** Type \`/help\` first whenever you are unsure which commands are available to your character.
        `,
    },
    {
        id: 10,
        title: "Help & Support",
        icon: "🆘",
        content: `
# Need Assistance?

Whether you're experiencing a technical issue, have an account question, or need to report suspicious activity, use the official support channels.

## Where to Get Help

### Discord Community
Visit the official Critical Conquer Discord server and check the appropriate help or support channel. Staff announcements may also contain solutions to common issues.

### Account Assistance
For account-related concerns, contact:

**Email:** CriticalConquer2025@gmail.com

When contacting support, explain the issue clearly and include relevant details that help the team understand what happened.

### Bug Reports
If you discover a bug or suspect an exploit, report it responsibly. Avoid abusing the issue or sharing exploit instructions publicly.

## Help Us Help You

When submitting a report, include:

- Your character name.
- A clear description of the problem.
- When the issue occurred.
- Screenshots or relevant details, when available.

Never send your password or other confidential account credentials to anyone claiming to be staff.

## Response Time

The support team aims to respond within 24 hours, although actual response times may vary depending on the number and complexity of requests.

**Support Tip:** Use official channels and be patient while your issue is being reviewed.
        `,
    },
    {
        id: 11,
        title: "Events & Tournaments",
        icon: "🎉",
        content: `
# Events, Competition, and Rewards

Take a break from routine hunting and put your skills to the test. Server events offer opportunities to meet other players, compete with rivals, and work together with your guild.

## Activities to Watch

### Daily EXP Bonuses
Check announcements for available experience bonuses and their schedules.

### Guild Wars
Team up with your guild, coordinate your strategy, and compete for control and recognition during scheduled Guild Wars.

### Special Quests
Explore activities such as Moonbox, CelestialStone, and BlueMouse quests when available on the server.

### PvP Tournaments
Test your build and combat skills in organized player-versus-player competitions. Check official announcements for tournament dates and rewards.

## How to Prepare

- Keep your equipment and inventory ready.
- Make sure you know the event's start time and entry requirements.
- Coordinate with your guild or party before major battles.
- Read the rules and reward details before participating.

## Where to Find Schedules

Check the in-game announcements and official Critical Conquer Discord for confirmed times, event rules, and any changes.

**Warrior's Tip:** Being prepared and communicating with your team can make a real difference when competition begins.
        `,
    },
    {
        id: 12,
        title: "Trading & Marketplace",
        icon: "💼",
        content: `
# Trading in Critical Conquer

A good trader understands item value, checks every deal, and knows when to buy, sell, or keep equipment. The Marketplace helps players discover available items and compare listings.

## Listing Your Items

1. Visit the Marketplace NPC or use the supported marketplace feature.
2. Select the item you want to sell.
3. Review the item's quality, upgrade level, gems, and other relevant attributes.
4. Set a reasonable price based on comparable listings.
5. Confirm the listing and check that the item appears correctly.

## Buying Equipment

- Compare similar items before spending your currency.
- Check item attributes and upgrade levels carefully.
- Verify the seller and the final price.
- Confirm all trade details before accepting.
- Avoid rushing into deals that seem suspicious.

## Protect Yourself From Scams

Never assume a player is trustworthy based on their name, guild, or claims. Review every trade window and make sure the correct items and amounts are displayed before confirming.

## Build Your Trading Strategy

Watch how prices change as players progress and events take place. Some items may be more desirable at certain times, but demand and prices are never guaranteed.

**Trader's Tip:** A fair price and a reliable reputation can help you build better relationships with other players.
        `,
    },
    {
        id: 13,
        title: "Frequently Asked Questions (FAQ)",
        icon: "❓",
        content: `
# Frequently Asked Questions

Find quick answers to common questions about getting started, account access, gameplay, and technical issues in Critical Conquer.

## How do I register an account?

Visit the official website and select **Register** from the navigation menu. Complete the required fields, provide a valid email address if requested, and follow the instructions to prepare your account and download the game client.

## Can I transfer characters or items from another server?

No. Character and item transfers from other servers are not supported. Every player begins their journey in Critical Conquer with a fresh start.

## What should I do if I find a bug or exploit?

Report it through the official Discord support channels or email CriticalConquer2025@gmail.com. Do not abuse the issue or distribute exploit instructions. Intentional exploitation may result in disciplinary action.

## Why is my ping high or my FPS low?

Try these troubleshooting steps:

- Use the official game client and launcher.
- Check your internet connection and server selection.
- Close unnecessary background applications.
- Review your in-game graphics settings.
- Check whether your device meets the game's requirements.

If the problem continues, contact support with details about your system and the issue.

## How do I join Guild Wars?

Watch official in-game announcements and Discord for the confirmed schedule and entry instructions. Be online before the event begins and follow the current instructions for entering the Guild War area.

## What if I forget my password?

Use the **Forgot Password** option on the official website's login page, if available. Make sure you can access the email address associated with your account.

If you cannot recover access, contact official support. Never share your password with another player or anyone claiming to be staff.

## Where can I find the latest server information?

Check the official website Wiki, server news, and Critical Conquer Discord announcements for current updates, event schedules, and gameplay information.

**Still have questions?** Visit the Help & Support guide for the available support channels.
        `,
    },
];

export default wikiData;